import {
  describeRecurrence,
  type CheckInTemplate,
  type DeliveryCapability,
  type ReminderSchedule,
  type Timestamp,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { Page, SubHeader } from '../../components/Layout.js'
import { useToast } from '../../components/Toast.js'
import { formatTime, formatUpcomingDay } from '../../format/dates.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Reminders.module.css'

/**
 * Reminder schedules (T-702).
 *
 * The design problem here is honesty, not layout. The app lets you configure
 * something it cannot yet do, and it has to do that without either hiding the
 * feature or implying it works — so the notice explaining that delivery arrives
 * with the mobile app is the first thing on the screen, visible **before** the
 * first schedule exists rather than discovered after one is made.
 *
 * The notice's text comes from the `Notifier` port, not from a constant here.
 * That is what stops a component hard-coding a platform fact: the native app
 * returns `available`, and this screen falls silent with nothing to delete.
 *
 * Nothing asks for notification permission. A granted permission would achieve
 * nothing on the web, and a dialog that buys nothing costs trust.
 */
export function RemindersScreen() {
  const { repos, clock, notifier } = useServices()
  const navigate = useNavigate()
  const toast = useToast()

  const [schedules, setSchedules] = useState<ReminderSchedule[] | null>(null)
  const [templates, setTemplates] = useState<Map<string, CheckInTemplate>>(new Map())
  const [next, setNext] = useState<Map<string, Timestamp>>(new Map())
  const [delivery, setDelivery] = useState<DeliveryCapability | null>(null)

  const reload = useCallback(async () => {
    const [all, allTemplates, upcoming, capability] = await Promise.all([
      repos.reminders.list(),
      repos.templates.list({ includeArchived: true }),
      repos.reminders.nextOccurrences(),
      notifier.delivery(),
    ])
    setSchedules(all)
    setTemplates(new Map(allTemplates.map((template) => [template.id, template])))
    setNext(upcoming)
    setDelivery(capability)
  }, [repos, notifier])

  useEffect(() => {
    void reload()
  }, [reload])

  const toggle = async (schedule: ReminderSchedule) => {
    const result = await repos.reminders.setEnabled(schedule.id, !schedule.enabled)
    if (!result.ok) {
      toast.show({ message: result.errors[0]?.message ?? '', tone: 'danger' })
      return
    }
    await reload()
  }

  const remove = async (schedule: ReminderSchedule) => {
    const result = await repos.reminders.remove(schedule.id)
    if (!result.ok) {
      toast.show({ message: result.errors[0]?.message ?? '', tone: 'danger' })
      return
    }
    // Cancelled through the port too. On the web that is a no-op, and a caller
    // tidying up should not have to know which platform it is on.
    await notifier.cancel(schedule.id)
    await reload()
    toast.show({
      message: `Deleted ${schedule.name}`,
      action: {
        label: 'Undo',
        onAction: () => {
          void repos.reminders
            .create({
              name: schedule.name,
              recurrence: schedule.recurrence,
              enabled: schedule.enabled,
              ...(schedule.templateId === undefined
                ? {}
                : { templateId: schedule.templateId }),
            })
            .then(reload)
        },
      },
    })
  }

  const now = clock.now()

  return (
    <Page>
      <SubHeader back={{ to: '/settings', label: 'Settings' }} />
      <h1>Reminders</h1>

      {/*
        First on the screen, and stated once rather than repeated per row.
        Someone who has not made a reminder yet is exactly the person about to
        make one and expect it to arrive.
      */}
      {delivery?.status === 'unavailable' && (
        <p className={styles['notice']} data-testid="delivery-notice">
          {delivery.reason}
        </p>
      )}

      {schedules === null ? (
        <p>Loading…</p>
      ) : schedules.length === 0 ? (
        <EmptyState
          title="No reminders yet"
          body="Set the times you want to be prompted to record something. They are saved on this device and carry over through a backup."
          action={
            <Button variant="primary" onClick={() => void navigate('/reminders/new')}>
              Add a reminder
            </Button>
          }
        />
      ) : (
        <>
          <div className={styles['list']} data-bleed>
            {schedules.map((schedule) => {
              const upcoming = next.get(schedule.id)
              const template =
                schedule.templateId === undefined
                  ? undefined
                  : templates.get(schedule.templateId)
              return (
                <div key={schedule.id} className={styles['row']} data-testid="reminder">
                  <button
                    type="button"
                    className={styles['open']}
                    onClick={() => void navigate(`/reminders/${schedule.id}`)}
                  >
                    <span className={styles['name']}>{schedule.name}</span>
                    <span className={styles['recurrence']}>
                      {describeRecurrence(schedule.recurrence)}
                    </span>
                    {template !== undefined && (
                      <span className={styles['template']}>{template.name}</span>
                    )}
                    {/*
                      Shown so the configuration can be checked without waiting
                      for it — and worded as *when it would be*, because on the
                      web nothing is going to arrive.
                    */}
                    <span className={styles['next']} data-testid="next-occurrence">
                      {schedule.enabled
                        ? upcoming === undefined
                          ? 'No upcoming time'
                          : `Next ${formatUpcomingDay(upcoming, now)} at ${formatTime(upcoming)}`
                        : 'Turned off'}
                    </span>
                  </button>

                  <div className={styles['actions']}>
                    <Button
                      aria-pressed={schedule.enabled}
                      onClick={() => void toggle(schedule)}
                    >
                      {schedule.enabled ? 'On' : 'Off'}
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={`Delete ${schedule.name}`}
                      onClick={() => void remove(schedule)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>

          <Button variant="primary" onClick={() => void navigate('/reminders/new')}>
            Add a reminder
          </Button>
        </>
      )}
    </Page>
  )
}
